import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Code2, Download, Eye, FileText, Loader2, Lock, RotateCw, Sparkles, X, XCircle } from "lucide-react";

import { api, API } from "@/lib/api";
import { StatusBadge } from "@/pages/Dashboard";
import { supportCode, matchesSupportCode } from "@/lib/supportCode";
import SupportCodeBadge from "@/components/SupportCodeBadge";

import SdiTimeline from "./invoice-review/SdiTimeline";
import FicSendModal from "./invoice-review/FicSendModal";
import XmlViewerModal from "./invoice-review/XmlViewerModal";
import SupplierSection from "./invoice-review/SupplierSection";
import InvoiceDataSection from "./invoice-review/InvoiceDataSection";
import LineItemsSection from "./invoice-review/LineItemsSection";
import TotalsSection from "./invoice-review/TotalsSection";
import ActionsFooter from "./invoice-review/ActionsFooter";
import { PdfEmbed } from "./invoice-review/ReviewFields";

/**
 * Banner mostrato quando una fattura arrivata via inbound email risulta
 * essere il duplicato byte-per-byte di un'altra già presente. In questo
 * stato NON è stata avviata l'estrazione (0 crediti spesi). L'utente
 * decide se processarla comunque (bottone → chiama /process-anyway →
 * consuma 1 credito) o ignorarla (torna alla lista).
 */
function DuplicatePendingBanner({ invoice, onProcessed }) {
  const [processing, setProcessing] = React.useState(false);
  const dupId = invoice.possible_duplicate_of;
  const process = async () => {
    setProcessing(true);
    try {
      await api.post(`/invoices/${invoice.id}/process-anyway`);
      toast.success("Fattura in elaborazione · 1 credito consumato");
      onProcessed?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore durante l'elaborazione");
    } finally {
      setProcessing(false);
    }
  };
  return (
    <div className="max-w-4xl mx-auto mt-6 mx-4 sm:mx-6 lg:mx-10 rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="duplicate-pending-banner">
      <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-amber-900">Fattura duplicata ricevuta via email</div>
        <div className="text-sm text-amber-800 mt-0.5">
          Questa fattura è arrivata via inbound ed è <strong>identica</strong> a{" "}
          {dupId ? (
            <Link to={`/app/invoices/${dupId}`} className="font-mono font-bold underline hover:no-underline">
              {supportCode(dupId)}
            </Link>
          ) : (
            "un&apos;altra già presente"
          )}
          . <strong>Non abbiamo consumato crediti</strong>: se è un vero duplicato ignorala. Se vuoi processarla comunque, clicca sotto — verrà consumato <strong>1 credito</strong>.
        </div>
        <div className="mt-3 flex gap-2">
          <button
            onClick={process}
            disabled={processing}
            data-testid="process-duplicate-anyway-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 disabled:opacity-70 transition-colors"
          >
            {processing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Processa comunque (1 credito)
          </button>
        </div>
      </div>
    </div>
  );
}

const EMPTY = {
  supplier_name: "", supplier_vat_id: "", supplier_address: "", supplier_city: "",
  supplier_zip: "", supplier_country: "", invoice_number: "", invoice_date: "",
  data_ricezione: "", data_autofattura: "", autofattura_number: "",
  currency: "EUR", total_amount: 0, total_amount_eur: 0,
  imponibile: 0, imposta: 0, aliquota_iva: 22, natura: "",
  service_description: "", is_service: true, is_eu_supplier: false,
  original_currency: "", fx_rate: 1, fx_date: "", fx_source: "",
  confidence: 0, line_items: [],
};

const EMPTY_LINE = {
  descrizione: "", prezzo_totale: 0, quantita: 1, prezzo_unitario: 0, aliquota_iva: 22, natura: "",
};

// Cheap client-side id generator for brand-new lines that haven't been
// persisted yet. Server-side re-assigns via `models.LineItem.id` on save.
const genLineId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function InvoiceReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [extracted, setExtracted] = useState(EMPTY);
  const [docType, setDocType] = useState("TD17");
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [xmlPreview, setXmlPreview] = useState("");
  const [xmlFilename, setXmlFilename] = useState("");
  const [showPdf, setShowPdf] = useState(false);
  const [showXml, setShowXml] = useState(false);
  const [refreshingFx, setRefreshingFx] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [showFicModal, setShowFicModal] = useState(false);
  // Ephemeral flag: turned on for ~3s after every save so the ActionsFooter
  // can flash a "XML aggiornato ora" pill accanto al bottone "Visualizza XML".
  // Rafforza visivamente che l'XML è vivo e sincronizzato con i dati correnti.
  const [xmlJustUpdated, setXmlJustUpdated] = useState(false);
  // Dirty state (Feb 29, 2026 v16 — richiesta cliente):
  // Se l'utente modifica un qualsiasi campo del form ma non salva, tutte
  // le azioni di invio (SDI/FIC/Manuale) e la vista XML devono essere
  // BLOCCATE finché non clicca "Salva modifiche". Evita che parta un
  // invio con dati non salvati (XML lato server ancora quello vecchio).
  const [dirty, setDirty] = useState(false);
  // Auto-save debounced (v16 enhancement): dopo 3s di inattività la
  // fattura viene salvata silenziosamente. `lastSavedAt` alimenta la pill
  // "Salvato · HH:MM" nel footer, `autoSaveError` cambia il colore a red
  // quando la rete è down così l'utente sa che deve riprovare a mano.
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [autoSaveError, setAutoSaveError] = useState(false);
  // Retry estrazione (v17j Feb 29, 2026): quando l'estrazione fallisce per
  // uno spike temporaneo di Gemini (503 UNAVAILABLE) o simili, il backend
  // marca `status=error`. L'utente ora ha un bottone "Riprova estrazione"
  // che ri-accoda il PDF senza consumare un secondo credito (il credito era
  // già stato addebitato al primo tentativo — è coperto dal flag
  // `credit_charged` sul doc).
  const [retrying, setRetrying] = useState(false);

  // Applica un invoice-payload al React state (usato sia dal load iniziale
  // sia dal save PUT così l'UI riflette IMMEDIATAMENTE i valori canonici del
  // server — inclusi `autofattura_number` allocato al primo save, timestamps
  // aggiornati, docType normalizzato, etc.).
  // Bug fix Feb 29, 2026 v16: prima solo `setInvoice(data)` veniva chiamato
  // dopo save → il form (bindato a `extracted`) mostrava ancora il valore
  // vuoto per `autofattura_number` finché l'utente non ricaricava a mano.
  const applyInvoiceData = useCallback((data) => {
    setInvoice(data);
    const merged = { ...EMPTY, ...(data.extracted || {}) };
    merged.line_items = (merged.line_items || []).map((li) => ({
      ...EMPTY_LINE,
      ...li,
      id: li?.id || genLineId(),
    }));
    const createdDate = (data.created_at || "").slice(0, 10) || new Date().toISOString().slice(0, 10);
    if (!merged.data_ricezione) merged.data_ricezione = createdDate;
    if (!merged.data_autofattura) merged.data_autofattura = merged.data_ricezione;
    setExtracted(merged);
    setDocType(data.document_type || "TD17");
    // Lo state è ora canonico e allineato al server → non più "dirty".
    setDirty(false);
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/invoices/${id}`);
      applyInvoiceData(data);
    } catch (err) {
      toast.error("Fattura non trovata");
      navigate("/app/invoices");
    }
  }, [id, navigate, applyInvoiceData]);

  const loadSuppliers = useCallback(async () => {
    try {
      const { data } = await api.get("/suppliers");
      setSuppliers(data || []);
    } catch (err) {
      // Non-critical: suppliers picker just stays empty. Log for debugging
      // but don't disturb the user — the review form remains usable.
      console.warn("[loadSuppliers] failed:", err?.response?.data?.detail || err.message);
    }
  }, []);

  useEffect(() => { load(); loadSuppliers(); }, [load, loadSuppliers]);

  // Auto-save debounced (v16 enhancement): salva silenziosamente 3s dopo
  // l'ultima modifica. Usiamo un ref per catturare l'ultima `save()` senza
  // metterla come dep del useEffect (altrimenti si ricrea ad ogni render e
  // il debounce non prende mai vita). Il timer si azzera ad ogni edit.
  const saveRef = useRef(null);
  saveRef.current = (opts) => save(opts);
  useEffect(() => {
    if (!dirty || saving || !invoice) return;
    // Non tentare autosave su fatture non editabili (lockState=locked).
    if (invoice?.lock_state && !invoice.lock_state.is_editable) return;
    const timer = setTimeout(async () => {
      try {
        await saveRef.current({ silent: true });
      } catch {
        setAutoSaveError(true);
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [dirty, extracted, docType, saving, invoice]);

  // beforeunload warning: avvisa se l'utente prova a chiudere la tab / navigare
  // altrove con modifiche non salvate. Se la fattura non è dirty, no-op.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => {
      // Modern browsers ignore the message but still show a generic prompt
      // if `preventDefault` is called and `returnValue` is set.
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  useEffect(() => {
    if (invoice?.status !== "extracting") return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [invoice?.status, load]);

  // SDI status refresh — dal Feb 2026 lo stato SDI ci arriva via webhook
  // (OpenAPI `customer-notification` event → `/api/sdi/webhook/openapi`).
  // v17e (Feb 29, 2026 — cliente report): il polling frontend interval è
  // stato rimosso perché generava 401 upstream continui per le fiscal_id
  // che nel modello stateless NON sono più nel nostro business_registry.
  // Il webhook è scoped all'account (non alla fiscal_id) → funziona sempre.
  // Manteniamo solo un one-shot on-mount come safety net (best-effort:
  // se OpenAPI 401 la funzione ora ritorna {} silenziosamente).
  useEffect(() => {
    if (!invoice?.sdi_uuid) return;
    const finalStates = ["RC", "NS", "MC", "DT"];
    const isFinal = finalStates.includes((invoice.sdi_status || "").toUpperCase());
    if (isFinal) return;
    // One-shot refresh al mount + al cambio di sdi_status. Nessun interval:
    // il webhook aggiorna il DB in push, poi il prossimo `load()` (o F5 o
    // "Aggiorna") mostra il nuovo stato.
    api
      .post(`/invoices/${id}/refresh-sdi-status`)
      .then(() => load())
      .catch((err) => {
        console.warn("[sdi-poll] mount refresh failed:", err?.response?.data?.detail || err.message);
      });
  }, [invoice?.sdi_uuid, invoice?.sdi_status, id, load]);

  // ---- Auto-sync effects: line items → imponibile → imposta → totale
  useEffect(() => {
    const items = extracted.line_items || [];
    if (items.length === 0) return;
    const sum = items.reduce((s, li) => s + (parseFloat(li.prezzo_totale) || 0), 0);
    const newImp = +sum.toFixed(2);
    setExtracted((p) => (p.imponibile === newImp ? p : { ...p, imponibile: newImp }));
  }, [extracted.line_items]);

  useEffect(() => {
    const imp = parseFloat(extracted.imponibile) || 0;
    const al = parseFloat(extracted.aliquota_iva) || 0;
    const newImposta = +(imp * al / 100).toFixed(2);
    setExtracted((p) => (p.imposta === newImposta ? p : { ...p, imposta: newImposta }));
  }, [extracted.imponibile, extracted.aliquota_iva]);

  useEffect(() => {
    const imp = parseFloat(extracted.imponibile) || 0;
    const imposta = parseFloat(extracted.imposta) || 0;
    const newTotal = +(imp + imposta).toFixed(2);
    setExtracted((p) => {
      if (p.total_amount === newTotal && p.total_amount_eur === newTotal) return p;
      return { ...p, total_amount: newTotal, total_amount_eur: newTotal };
    });
  }, [extracted.imponibile, extracted.imposta]);

  const upd = (k) => (e) => {
    const raw = e.target?.value ?? e;
    let val;
    if (e.target?.type === "number") val = parseFloat(raw || 0);
    else if (k === "aliquota_iva") val = parseFloat(raw || 0);
    else val = raw;
    setExtracted((p) => ({ ...p, [k]: val }));
    setDirty(true);
  };

  const updateLine = (idx, key, value) => {
    setExtracted((p) => {
      const items = [...(p.line_items || [])];
      const next = { ...items[idx], [key]: value };
      if (key === "prezzo_totale") next.prezzo_unitario = value;
      items[idx] = next;
      return { ...p, line_items: items };
    });
    setDirty(true);
  };
  const addLine = () => {
    setExtracted((p) => ({
      ...p,
      line_items: [
        ...(p.line_items || []),
        { ...EMPTY_LINE, id: genLineId(), aliquota_iva: p.aliquota_iva || 22 },
      ],
    }));
    setDirty(true);
  };
  const removeLine = (idx) => {
    setExtracted((p) => ({ ...p, line_items: (p.line_items || []).filter((_, i) => i !== idx) }));
    setDirty(true);
  };

  const applySupplier = (idxStr) => {
    const idx = parseInt(idxStr, 10);
    if (isNaN(idx) || !suppliers[idx]) return;
    const s = suppliers[idx];
    setExtracted((p) => ({ ...p, ...s }));
    setDirty(true);
    toast.success(`Precompilato con dati di ${s.supplier_name}`);
  };

  const sommaRighe = useMemo(
    () => (extracted.line_items || []).reduce((s, li) => s + (parseFloat(li.prezzo_totale) || 0), 0),
    [extracted.line_items]
  );

  const save = async ({ redirect = false, silent = false } = {}) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/invoices/${id}`, {
        extracted,
        document_type: docType,
        status: invoice.status === "extracted" ? "reviewed" : invoice.status,
      });
      // Ri-sincronizza TUTTO lo stato pagina con la risposta canonica del
      // server (invoice + extracted + docType). Prima solo `setInvoice(data)`
      // veniva chiamato → il form continuava a mostrare `autofattura_number`
      // vuoto anche se il backend l'aveva appena allocato (bug Feb 29 v16).
      applyInvoiceData(data);
      // Registra timestamp per la pill "Salvato · HH:MM" nel footer.
      setLastSavedAt(new Date());
      setAutoSaveError(false);
      // Invalida la cache in-memoria dell'XML: il backend l'ha appena
      // rigenerato, quindi qualsiasi apertura successiva del modal deve
      // rifetchare per mostrare la versione aggiornata.
      setXmlPreview("");
      setXmlFilename("");
      // Flash "XML aggiornato ora" pill per 3s.
      if (data.xml_path) {
        setXmlJustUpdated(true);
        setTimeout(() => setXmlJustUpdated(false), 3000);
      }
      if (!silent) {
        toast.success(data.xml_path ? "Modifiche salvate · XML aggiornato" : "Modifiche salvate");
      }
      if (redirect) navigate("/app/invoices");
      return data;
    } catch (err) {
      if (!silent) {
        toast.error(err?.response?.data?.detail || "Errore nel salvataggio");
      }
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // "Invia a Fatture in Cloud": salva prima (rigenera XML), poi apre il modal
  // di invio così l'XML che verrà spedito è sempre aggiornato ai dati correnti.
  const openFic = async () => {
    try {
      await save({ silent: true });
      setShowFicModal(true);
    } catch {
      // Toast già mostrato via save loud path — qui silent, quindi mostriamo
      // un fallback esplicito.
      toast.error("Salvataggio delle modifiche non riuscito · impossibile procedere");
    }
  };

  // Elimina fattura (v17 Feb 29, 2026 richiesta cliente): backend impone che
  // il documento non sia mai stato trasmesso (409 altrimenti). Il progressivo
  // eventuale viene rollbackato server-side, i file storage puliti best-effort,
  // il credito consumato NON viene rimborsato.
  const remove = async () => {
    try {
      const { data } = await api.delete(`/invoices/${id}`);
      toast.success("Fattura eliminata.", {
        description: data?.rolled_back_number
          ? `Il numero ${data.rolled_back_number} tornerà disponibile per la prossima fattura.`
          : "Il credito consumato non è rimborsabile (il servizio è già stato erogato).",
      });
      navigate("/app/invoices");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nella cancellazione");
      throw err;
    }
  };

  // Retry estrazione — ri-accoda il PDF al pipeline Gemini. Usato sui
  // fallimenti da 503/rate-limit di Gemini che si risolvono da soli in
  // pochi secondi. Il backend garantisce che il credito NON viene ricaricato
  // (flag `credit_charged` sul doc).
  const retryExtract = async () => {
    setRetrying(true);
    try {
      const { data } = await api.post(`/invoices/${id}/retry-extract`);
      if (data?.queued === false) {
        toast.info("Un'estrazione è già in corso.");
      } else {
        toast.success("Estrazione ri-avviata · nessun credito aggiuntivo consumato.");
      }
      await load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) {
        toast.error(detail || "Impossibile rilanciare l'estrazione in questo stato.");
      } else {
        toast.error(detail || "Errore durante il retry dell'estrazione");
      }
    } finally {
      setRetrying(false);
    }
  };

  const generateXml = async () => {
    // Legacy handler — il bottone "Genera XML" è stato rimosso (Feb 29 v10):
    // ogni "Salva modifiche" rigenera già l'XML lato backend. Manteniamo la
    // funzione solo come safety-net per eventuali automation vecchie.
    setGenerating(true);
    try {
      await save({ silent: true });
      const { data } = await api.post(`/invoices/${id}/generate-xml`);
      setXmlPreview(data.xml_content);
      setXmlFilename(data.xml_filename);
      setShowXml(true);
      toast.success("XML generato con successo!");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nella generazione XML");
    } finally {
      setGenerating(false);
    }
  };

  const refreshFx = async () => {
    setRefreshingFx(true);
    try {
      const { data } = await api.post(`/invoices/${id}/refresh-fx`);
      toast.success(`Tasso aggiornato: ${extracted.original_currency}→EUR ${data.fx_rate.toFixed(4)}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore aggiornamento tasso");
    } finally {
      setRefreshingFx(false);
    }
  };

  const downloadXml = () => {
    const token = localStorage.getItem("auth_token");
    fetch(`${API}/invoices/${id}/xml`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = xmlFilename || `${id}.xml`;
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  // Apre il modal "Visualizza XML". Rifetcha SEMPRE il contenuto dal backend
  // così mostra la versione più recente (il backend può aver rigenerato l'XML
  // dopo l'ultimo save o refresh FX). Il round-trip è cheap (~50ms locale)
  // e garantisce zero stale-state.
  const openXmlViewer = async () => {
    if (!invoice?.xml_path) return;
    setShowXml(true);
    try {
      const token = localStorage.getItem("auth_token");
      const r = await fetch(`${API}/invoices/${id}/xml`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-store",
      });
      if (r.ok) {
        const text = await r.text();
        const disp = r.headers.get("content-disposition") || "";
        const m = disp.match(/filename="?([^";]+)"?/i);
        setXmlPreview(text);
        if (m && m[1]) setXmlFilename(m[1]);
      } else {
        toast.error("Impossibile caricare l'XML");
      }
    } catch {
      toast.error("Errore nel caricamento dell'XML");
    }
  };

  if (!invoice) {
    return (
      <div className="p-10 grid place-items-center h-screen">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="invoice-review-page">
      {/* Editorial header */}
      <div className="bg-white border-b border-[#E8E4D9] px-4 sm:px-6 lg:px-10 py-5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/app/invoices" className="p-2 rounded-lg hover:bg-[#F5F2EA] text-[#4A4A4A] transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#1D4ED8] font-bold mb-0.5">
              Autofattura
            </div>
            <h1 className="font-display font-extrabold text-xl sm:text-3xl tracking-tighter truncate leading-none text-[#0A0A0A]" data-testid="page-title">
              {extracted.autofattura_number ? (
                <>
                  <span className="font-serif-accent text-[#1D4ED8]">#</span>
                  {extracted.autofattura_number}
                </>
              ) : (
                <span className="text-[#737373] italic font-normal text-lg">Numero non ancora assegnato</span>
              )}
            </h1>
            <div className="text-xs text-[#737373] flex items-center gap-2 mt-1.5 flex-wrap">
              <SupportCodeBadge invoiceId={id} size="md" data-testid="invoice-support-code" />
              <StatusBadge status={invoice.status} />
              {extracted.confidence ? (
                <span className="font-mono">confidence {Math.round(extracted.confidence * 100)}%</span>
              ) : null}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {invoice.file_path && (
            <button
              onClick={() => setShowPdf(true)}
              data-testid="toggle-pdf-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#E8E4D9] bg-white text-sm hover:bg-[#F5F2EA] transition-colors"
            >
              <Eye className="w-4 h-4" /> Vedi PDF
            </button>
          )}
          <Link
            to="/app/invoices"
            className="hidden sm:inline-flex text-sm text-[#4A4A4A] hover:text-[#0A0A0A] items-center gap-1 px-3 py-2 rounded-full hover:bg-[#F5F2EA] transition-colors"
            data-testid="back-to-list"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Torna all&apos;elenco
          </Link>
        </div>
      </div>

      {/* Duplicate-pending banner (inbound email dedup soft-block) */}
      {invoice.status === "duplicate_pending" && (
        <DuplicatePendingBanner invoice={invoice} onProcessed={load} />
      )}
      {/* Duplicate warning banner (force-uploaded manual duplicate OR post-extraction combo match) */}
      {invoice.status !== "duplicate_pending" && invoice.possible_duplicate_of && (
        <div className="max-w-4xl mx-auto mt-6 mx-4 sm:mx-6 lg:mx-10 rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="duplicate-warning-banner">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-amber-900">
              {invoice.duplicate_reason === "same_supplier_and_number"
                ? "Sospetto duplicato · stesso fornitore e numero fattura"
                : "Potenziale duplicato · contenuto identico"}
            </div>
            <div className="text-sm text-amber-800 mt-0.5">
              {invoice.duplicate_reason === "same_supplier_and_number" ? (
                <>
                  Esiste già una fattura con lo <strong>stesso fornitore</strong> (P.IVA {extracted.supplier_vat_id || "—"}) e lo{" "}
                  <strong>stesso numero</strong> ({extracted.invoice_number || "—"}) ma con PDF diverso:{" "}
                  <Link to={`/app/invoices/${invoice.possible_duplicate_of}`} className="font-mono font-bold underline hover:no-underline" data-testid="duplicate-warning-link">
                    {supportCode(invoice.possible_duplicate_of)}
                  </Link>
                  . Potrebbe essere una riemissione dello stesso documento. Verifica prima di procedere all&apos;invio SDI.
                </>
              ) : (
                <>
                  Un altro documento identico è già presente:{" "}
                  <Link to={`/app/invoices/${invoice.possible_duplicate_of}`} className="font-mono font-bold underline hover:no-underline" data-testid="duplicate-warning-link">
                    {supportCode(invoice.possible_duplicate_of)}
                  </Link>
                  . Se questa era la fattura giusta ignora l&apos;avviso; se è stata caricata per errore, contatta l&apos;admin per la cancellazione.
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Banners */}
      {invoice.status === "extracting" && (
        <div className="max-w-4xl mx-auto mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-center gap-3" data-testid="extracting-banner">
          <Loader2 className="w-5 h-5 text-amber-700 animate-spin" />
          <div className="flex-1">
            <div className="font-medium text-amber-900">L&apos;AI sta estraendo i dati…</div>
            <div className="text-sm text-amber-800">Solitamente 10-30 secondi.</div>
          </div>
        </div>
      )}
      {invoice.status === "error" && (
        <div className="max-w-4xl mx-auto mt-6 rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3 flex-wrap" data-testid="error-banner">
          <AlertTriangle className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-red-900">Estrazione fallita</div>
            <div className="text-sm text-red-800 break-words">{invoice.error_message}</div>
            <div className="mt-1 text-xs text-red-700/80">
              Errori come <span className="font-mono">503 UNAVAILABLE</span> sono spike temporanei del servizio AI.
              Solitamente si risolvono in pochi secondi — riprova senza costi aggiuntivi.
            </div>
          </div>
          <button
            type="button"
            onClick={retryExtract}
            disabled={retrying}
            data-testid="btn-retry-extract"
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-red-600 text-white text-xs font-bold hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            {retrying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
            Riprova estrazione
          </button>
        </div>
      )}

      {/* SDI rejection banner — user can now edit + resend */}
      {invoice.lock_state?.sdi_rejected && (
        <div className="max-w-4xl mx-auto mt-6 mx-4 sm:mx-6 lg:mx-10 rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3" data-testid="sdi-rejected-banner">
          <XCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-red-900">SDI ha scartato la fattura</div>
            <div className="text-sm text-red-800 mt-0.5">
              Correggi i dati indicati nel motivo del rifiuto (ti abbiamo mandato una email), poi rigenera l&apos;XML e re-invia allo SDI.
            </div>
          </div>
        </div>
      )}

      {/* Locked banner — invoice has been transmitted, no more edits */}
      {invoice.lock_state && !invoice.lock_state.is_editable && (
        <div className="max-w-4xl mx-auto mt-6 mx-4 sm:mx-6 lg:mx-10 rounded-xl border border-[#E8E4D9] bg-[#F5F2EA] p-4 flex items-start gap-3" data-testid="locked-banner">
          <Lock className="w-5 h-5 text-[#0A0A0A] shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-[#0A0A0A]">Fattura trasmessa · non modificabile</div>
            <div className="text-sm text-[#4A4A4A] mt-0.5">
              {invoice.lock_state.sdi_sent && "Inviata allo SDI. "}
              {invoice.lock_state.fic_sent && "Inviata a Fatture in Cloud. "}
              Le autofatture sono documenti fiscali immutabili una volta trasmessi. Se lo SDI la scarta, potrai correggerla e re-inviarla.
            </div>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="p-4 sm:p-6 lg:p-10">
        <fieldset
          disabled={invoice.lock_state && !invoice.lock_state.is_editable}
          className="border-0 p-0 m-0 disabled:opacity-70 group"
          data-testid="invoice-edit-fieldset"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <SupplierSection
              extracted={extracted}
              upd={upd}
              suppliers={suppliers}
              applySupplier={applySupplier}
            />

            <div className="bg-white rounded-2xl shadow-sm border border-[#E8E4D9] p-5 sm:p-6">
              <InvoiceDataSection
                extracted={extracted}
                upd={upd}
                docType={docType}
                setDocType={setDocType}
                refreshFx={refreshFx}
                refreshingFx={refreshingFx}
              />

              <LineItemsSection
                extracted={extracted}
                updateLine={updateLine}
                addLine={addLine}
                removeLine={removeLine}
                sommaRighe={sommaRighe}
              />
            </div>
          </div>

          <TotalsSection extracted={extracted} upd={upd} />
        </fieldset>

        <ActionsFooter
          invoice={invoice}
          saving={saving}
          generating={generating}
          lockState={invoice.lock_state}
          onCancel={() => navigate("/app/invoices")}
          onGenerateXml={generateXml}
          onDownloadXml={downloadXml}
          onOpenXml={openXmlViewer}
          onOpenFic={openFic}
          onSave={(opts) => save(opts)}
          onSent={load}
          onDelete={remove}
          xmlJustUpdated={xmlJustUpdated}
          dirty={dirty}
          lastSavedAt={lastSavedAt}
          autoSaveError={autoSaveError}
        />
      </div>

      {/* PDF drawer — only when the PDF is still on disk (removed after XML gen) */}
      {showPdf && invoice.file_path && (
        <div
          className="fixed inset-y-0 right-0 w-full lg:w-[560px] z-40 bg-white border-l border-[#E8E4D9] shadow-xl flex flex-col"
          data-testid="pdf-drawer"
        >
          <div className="flex items-center justify-between p-3 border-b border-[#E8E4D9]">
            <div className="flex items-center gap-2 text-sm font-medium">
              <FileText className="w-4 h-4" /> {invoice.file_name}
            </div>
            <button onClick={() => setShowPdf(false)} className="p-1.5 rounded hover:bg-white">
              <X className="w-4 h-4" />
            </button>
          </div>
          <PdfEmbed url={`${API}/invoices/${id}/pdf`} token={localStorage.getItem("auth_token")} />
        </div>
      )}

      {/* XML viewer modal — tab anteprima leggibile + sorgente XML */}
      <XmlViewerModal
        open={showXml}
        onClose={() => setShowXml(false)}
        invoice={invoice}
        extracted={extracted}
        xmlContent={xmlPreview}
        xmlFilename={xmlFilename}
        onDownload={downloadXml}
      />

      {/* Fatture in Cloud send modal */}
      <FicSendModal
        open={showFicModal}
        onClose={() => setShowFicModal(false)}
        invoiceId={id}
        onSent={() => load()}
      />

      {/* SDI Timeline (auto-hidden if no notifications) */}
      <SdiTimeline invoiceId={id} />
    </div>
  );
}
